import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Skeleton, Typography } from "@mui/material";

import entityBGImage from "../../../assets/images/profiles/entity-profile-bg.png";
import { followEntity, unFollowEntity } from "../../../api/entity";
import { useInvalidateMyQuery } from "../../../hooks/useInvalidateMyQuery";
import { colors } from "../../../theme/palette";
import { UserInfo } from "../../../types";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { VerifiedIcon } from "../../../assets/icons/MUI/LayoutIcons";

import EditProfileModal from "./EditProfileModal";

const EntityHeader = ({
  entityInfo,
  showModal,
  setShowModal,
  isOwnProfile,
  isEntityInfoLoading,
}: {
  entityInfo?: UserInfo;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isOwnProfile: boolean;
  isEntityInfoLoading: boolean;
}) => {
  const { entityId } = useParams();
  const [isUserFollowing, setIsUserFollowing] = useState<boolean | undefined>(entityInfo?.isFollowing);

  const invalidateNetworkFollowersList = useInvalidateMyQuery("NetworkList");

  const {
    fullName,
    firstTwoChr,
    media,
    followersCount,
    role,
    isFollowing,
    isManagedByEntity,
    countryName,
    isVerified,
  } = useDataFormatter(entityInfo);

  useEffect(() => {
    setIsUserFollowing(entityInfo?.isFollowing);
  }, [isFollowing]);

  const { t } = useTranslation();

  if (isEntityInfoLoading || !entityInfo) return <EntityHeaderSkeleton />;

  return (
    <Box
      sx={{
        height: "fit-content",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: colors.White,
        position: "relative",
      }}
    >
      <Typography
        variant="body13Regular"
        sx={{
          color: colors.Dark1000,
          padding: "3px 21px",
          borderRadius: "8.5px",
          background: "#FFEB33",
          position: "absolute",
          top: "20px",
          right: "12px",
        }}
      >
        {isManagedByEntity
          ? t("pages.entity.thisPageIsManagedByEntity")
          : t("pages.entity.thisPageIsNotManagedByEntity")}
      </Typography>
      <Box
        sx={{
          height: "145px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          backgroundImage: `url(${entityBGImage})`,
        }}
      />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: "15px 17px 27px",
        }}
      >
        <Box
          sx={{
            width: "fit-content",
            position: "relative",
            paddingBottom: "10px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-55px",
            }}
          >
            <Avatar
              sx={{
                width: "110px",
                height: "110px",
              }}
              src={media}
            >
              {firstTwoChr}
            </Avatar>
            {isVerified && (
              <VerifiedIcon
                sx={{
                  position: "absolute",
                  top: "100px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: "10",
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "7px",
              paddingInlineStart: "150px",
            }}
          >
            <Typography variant="body16Bold" sx={{ fontWeight: "700" }}>
              {fullName}
            </Typography>
            <Typography
              variant="body14Bold"
              sx={{
                fontWeight: "600",
                color: colors.Dark600,
              }}
            >
              {role}
              {countryName && ` . ${countryName}`}
              {Number(followersCount) ? ` . ${followersCount} ${t("pages.entity.followers")}` : ""}
            </Typography>
          </Box>
        </Box>

        {isOwnProfile ? (
          <>
            <Button
              onClick={() => setShowModal(true)}
              variant="primary-sm"
              sx={{
                height: "fit-content",
                whiteSpace: "nowrap",

                fontFamily: "HeadingNowVarTrial",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "20px",
                minWidth: "100px",
              }}
            >
              {t("pages.entity.editProfile")}
            </Button>
            <EditProfileModal openModal={showModal} entityInfo={entityInfo} closeModal={() => setShowModal(false)} />
          </>
        ) : isUserFollowing ? (
          <Button
            variant="secondary-md"
            sx={{
              height: "fit-content",
              whiteSpace: "nowrap",
            }}
            onClick={async () => {
              if (!entityId) return;
              const isFollowing = await unFollowEntity(entityId);
              if (!isFollowing) {
                setIsUserFollowing((prevIsUserFollowing) => !prevIsUserFollowing);
                invalidateNetworkFollowersList();
              }
            }}
          >
            {t("pages.entity.unfollow")}
          </Button>
        ) : (
          <Button
            variant="primary-sm"
            sx={{
              height: "fit-content",
            }}
            onClick={async () => {
              if (!entityId) return;
              const isFollowing = await followEntity(entityId);
              if (isFollowing) {
                setIsUserFollowing((prevIsUserFollowing) => !prevIsUserFollowing);
                invalidateNetworkFollowersList();
              }
            }}
          >
            {t("pages.entity.follow")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EntityHeader;

export const EntityHeaderSkeleton = () => {
  return (
    <Box
      sx={{
        height: "fit-content",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: colors.White,
        position: "relative",
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          position: "absolute",
          top: "20px",
          right: "12px",
          borderRadius: "8.5px",
          width: "160px",
          height: "25px",
        }}
      />

      <Box
        sx={{
          height: "145px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          backgroundImage: `url(${entityBGImage})`,
        }}
      />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: "15px 17px 27px",
        }}
      >
        <Box
          sx={{
            width: "fit-content",
            position: "relative",
            paddingBottom: "10px",
          }}
        >
          <Skeleton
            variant="circular"
            width={110}
            height={110}
            sx={{
              position: "absolute",
              top: "-55px",
              zIndex: "10",
              background: colors.Gray200,
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "7px",
              paddingInlineStart: "150px",
            }}
          >
            <Skeleton variant="text" width="75px" height={30} />
            <Skeleton variant="text" width="100px" height={20} />
          </Box>
        </Box>

        <Skeleton
          variant="rectangular"
          width="80px"
          height="35px"
          sx={{
            borderRadius: "50px",
          }}
        />
      </Box>
    </Box>
  );
};
